import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { storagePaginationValues } from "../../utils/constants";

interface PaginationControlsProps {
  currentLimit: number;
  onLimitChange: (limit: number) => void;
  totalItems?: number;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  showLimitSelector?: boolean;
  showPageInfo?: boolean;
  disabled?: boolean;
  size?: "small" | "medium" | "large";
  pageTokens?: string[]; // Array of continuation tokens for each page
  hasNextPage?: boolean; // Whether there's a continuation token for next page
  borderStyle?: object;
  buttonStyle?: object;
}

const PaginationControls: React.FC<PaginationControlsProps> = ({
  currentLimit,
  onLimitChange,
  totalItems,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  showLimitSelector = true,
  showPageInfo = true,
  disabled = false,
  size = "medium",
  pageTokens = [],
  hasNextPage = false,
  borderStyle = {},
  buttonStyle = {}
}) => {
  const handleLimitChange = (event: SelectChangeEvent<number>) => {
    const newLimit = event.target.value as number;
    onLimitChange(newLimit);
  };

  const handlePageChange = (page: number) => {
    if (onPageChange && page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  const handlePageNumberClick = (pageNumber: number) => {
    if (onPageChange && pageNumber >= 1 && pageNumber <= totalPages) {
      onPageChange(pageNumber);
    }
  };

  const getButtonSize = () => {
    switch (size) {
      case "small":
        return "small";
      case "large":
        return "medium";
      default:
        return "medium";
    }
  };

  const getTypographyVariant = () => {
    switch (size) {
      case "small":
        return "body2";
      case "large":
        return "h6";
      default:
        return "body1";
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        padding: 2,
        borderTop: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        ...borderStyle
      }}
    >
      {/* Left side - Limit selector */}
      {showLimitSelector && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant={getTypographyVariant()} color="text.secondary">
            Items per page:
          </Typography>
          <FormControl size={getButtonSize()} disabled={disabled}>
            <Select
              value={currentLimit}
              onChange={handleLimitChange}
              sx={{ minWidth: 80 }}
            >
              {storagePaginationValues.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Center - Page info */}
      {/* {showPageInfo && totalItems !== undefined && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant={getTypographyVariant()} color="text.secondary">
            {totalItems > 0
              ? `Showing ${(currentPage - 1) * currentLimit + 1} to ${Math.min(
                  currentPage * currentLimit,
                  totalItems
                )} of ${totalItems} items`
              : "No items to display"}
          </Typography>
        </Box>
      )} */}

      {/* Right side - Page navigation */}
      {onPageChange && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant={getTypographyVariant()} color="text.secondary">
            Page {currentPage} of {totalPages}
          </Typography>

          <ButtonGroup size={getButtonSize()}>
            <Tooltip title="First page">
              {currentPage === 1 || disabled ? (
                <span>
                  <Button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1 || disabled}
                    sx={{
                      minWidth: 40,
                      backgroundColor: "transparent",
                      color: "inherit",
                      border: "none",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "action.hover"
                      },
                      "&:disabled": {
                        backgroundColor: "transparent",
                        color: "action.disabled"
                      },
                    }}
                  >
                    {"<<"}
                  </Button>
                </span>
              ) : (
                <Button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1 || disabled}
                  sx={{
                    minWidth: 40,
                    backgroundColor: "transparent",
                    color: "inherit",
                    border: "none",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "action.hover"
                    },
                    "&:disabled": {
                      backgroundColor: "transparent",
                      color: "action.disabled"
                    }
                  }}
                >
                  {"<<"}
                </Button>
              )}
            </Tooltip>

            <Tooltip title="Previous page">
              {currentPage === 1 || disabled ? (
                <span>
                  <Button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1 || disabled}
                    sx={{
                      minWidth: 40,
                      backgroundColor: "transparent",
                      color: "inherit",
                      border: "none",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "action.hover"
                      },
                      "&:disabled": {
                        backgroundColor: "transparent",
                        color: "action.disabled"
                      }
                    }}
                  >
                    {"<"}
                  </Button>
                </span>
              ) : (
                <Button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || disabled}
                  sx={{
                    minWidth: 40,
                    backgroundColor: "transparent",
                    color: "inherit",
                    border: "none",
                    boxShadow: "none",
                    ...buttonStyle,
                    "&:hover": {
                      backgroundColor: "rgba(31,93,107,0.1)"
                    },
                    "&:disabled": {
                      backgroundColor: "transparent",
                      color: "action.disabled"
                    }

                  }}
                >
                  {"<"}
                </Button>
              )}
            </Tooltip>

            {/* Page number buttons */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
              let pageNumber: number;
              if (totalPages <= 5) {
                pageNumber = index + 1;
              } else if (currentPage <= 3) {
                pageNumber = index + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNumber = totalPages - 4 + index;
              } else {
                pageNumber = currentPage - 2 + index;
              }

              return (
                <Tooltip key={pageNumber} title={`Page ${pageNumber}`}>
                  {disabled ? (
                    <span>
                      <Button
                        onClick={() => handlePageNumberClick(pageNumber)}
                        disabled={disabled}
                        sx={{
                          minWidth: 40,
                          backgroundColor: currentPage === pageNumber ? "primary.main" : "transparent",
                          color: currentPage === pageNumber ? "primary.contrastText" : "inherit",
                          border: "none",
                          boxShadow: "none",
                          ...buttonStyle,
                          "&:hover": {
                            backgroundColor: currentPage === pageNumber ? "primary.main" : "action.hover"
                          },
                          "&:disabled": {
                            backgroundColor: currentPage === pageNumber ? "primary.main" : "transparent",
                            color: currentPage === pageNumber ? "primary.contrastText" : "action.disabled"
                          }
                        }}
                      >
                        {pageNumber}
                      </Button>
                    </span>
                  ) : (
                    <Button
                      onClick={() => handlePageNumberClick(pageNumber)}
                      disabled={disabled}
                      sx={{
                        minWidth: 40,
                        backgroundColor: currentPage === pageNumber ? "primary.main" : "transparent",
                        color: currentPage === pageNumber ? "primary.contrastText" : "inherit",
                        border: "none",
                        boxShadow: "none",
                        ...buttonStyle,
                        "&:hover": {
                          backgroundColor: currentPage === pageNumber ? "primary.main" : "action.hover"
                        },
                        "&:disabled": {
                          backgroundColor: currentPage === pageNumber ? "primary.main" : "transparent",
                          color: currentPage === pageNumber ? "primary.contrastText" : "action.disabled"
                        }
                      }}
                    >
                      {pageNumber}
                    </Button>
                  )}
                </Tooltip>
              );
            })}

            {hasNextPage && (
              <Tooltip title="Next page">
                {currentPage === totalPages || disabled ? (
                  <span>
                    <Button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages || disabled}
                      sx={{
                        minWidth: 40,
                        backgroundColor: "transparent",
                        color: "inherit",
                        border: "none",
                        boxShadow: "none",
                        ...buttonStyle,
                        "&:hover": {
                          backgroundColor: "action.hover"
                        },
                        "&:disabled": {
                          backgroundColor: "transparent",
                          color: "action.disabled"
                        }
                      }}
                    >
                      {">"}
                    </Button>
                  </span>
                ) : (
                  <Button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages || disabled}
                    sx={{
                      minWidth: 40,
                      backgroundColor: "transparent",
                      color: "inherit",
                      border: "none",
                      boxShadow: "none",
                      ...buttonStyle,
                      "&:hover": {
                        backgroundColor: "action.hover"
                      },
                      "&:disabled": {
                        backgroundColor: "transparent",
                        color: "action.disabled"
                      }
                    }}
                  >
                    {">"}
                  </Button>
                )}
              </Tooltip>
            )}

            {hasNextPage && totalPages > currentPage && (
              <Tooltip title="Last page">
                {currentPage === totalPages || disabled ? (
                  <span>
                    <Button
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages || disabled}
                      sx={{
                        minWidth: 40,
                        backgroundColor: "transparent",
                        color: "inherit",
                        border: "none",
                        boxShadow: "none",
                        ...buttonStyle,
                        "&:hover": {
                          backgroundColor: "action.hover"
                        },
                        "&:disabled": {
                          backgroundColor: "transparent",
                          color: "action.disabled"
                        }
                      }}
                    >
                      {">>"}
                    </Button>
                  </span>
                ) : (
                  <Button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages || disabled}
                    sx={{
                      minWidth: 40,
                      backgroundColor: "transparent",
                      color: "inherit",
                      border: "none",
                      boxShadow: "none",
                      ...buttonStyle,
                      "&:hover": {
                        backgroundColor: "action.hover"
                      },
                      "&:disabled": {
                        backgroundColor: "transparent",
                        color: "action.disabled"
                      }
                    }}
                  >
                    {">>"}
                  </Button>
                )}
              </Tooltip>
            )}
          </ButtonGroup>
        </Box>
      )}
    </Box>
  );
};

export default PaginationControls;
